import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { commerceAddToCart } from "@/lib/commerceEvents";
import { toast } from "sonner";
import { OFFER } from "@/config/offerConfig";

const CartContext = createContext(null);
// Sept 2026 perf split — `CartActionsContext` holds the stable action
// functions (all wrapped in useCallback with [] deps, so they never
// change reference). ProductCard reads ONLY from this — it no longer
// re-renders when cart state changes. Drops /build-your-own TBT from
// ~3.1s to sub-300ms on mid-tier Android because 22 ProductCards no
// longer all re-render on every `addItem`. `useCart()` still returns
// state+actions for the Cart page + Header + MobileCartBar where the
// full state IS needed.
const CartActionsContext = createContext(null);
const STORAGE_KEY = "tbb_cart_v1";
const CAPTURE_EMAIL_KEY = "tbb_capture_email"; // set by EmailCapturePopup on submit
const CAPTURE_NAME_KEY = "tbb_capture_name";

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : { items: [], subscription: OFFER.subscribeDefaultOn, frequency: "monthly" };
    if (parsed.subscription == null) parsed.subscription = OFFER.subscribeDefaultOn;
    // Sept 2026 (Part 2.6) safety net: merge any duplicate (slug, kind)
    // entries that could have slipped through from an older build. Session
    // recordings showed two rows of the same SKU appearing after a hot-
    // reload race on multi-tab shoppers.
    const byKey = new Map();
    for (const it of parsed.items || []) {
      const k = `${it.kind}:${it.slug}`;
      const existing = byKey.get(k);
      if (existing) {
        existing.quantity = (existing.quantity || 0) + (it.quantity || 0);
      } else {
        byKey.set(k, { ...it });
      }
    }
    parsed.items = Array.from(byKey.values()).filter((x) => (x.quantity || 0) > 0);
    return parsed;
  } catch {
    return { items: [], subscription: OFFER.subscribeDefaultOn, frequency: "monthly" };
  }
};

export const CartProvider = ({ children }) => {
  const [state, setState] = useState(load);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const { user } = useAuth() || {};
  const snapshotTimer = useRef(null);
  // Mirror of state.items so `addItem` can detect a duplicate line
  // synchronously (outside the functional setState) and tell the caller.
  const itemsRef = useRef(state.items);
  useEffect(() => { itemsRef.current = state.items; }, [state.items]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (err) {
      // localStorage can throw if disabled (Safari private mode) or quota exceeded.
      // Non-fatal — cart survives in-memory for this session.
      console.warn("[cart] localStorage persist failed:", err);
    }
  }, [state]);

  // Abandoned-cart snapshot: debounced fire whenever items change AND we have an email.
  useEffect(() => {
    if (snapshotTimer.current) clearTimeout(snapshotTimer.current);
    if (!state.items.length) return;
    const email = user?.email || localStorage.getItem(CAPTURE_EMAIL_KEY) || null;
    if (!email) return;
    const firstName = user?.name?.split(" ")[0] || localStorage.getItem(CAPTURE_NAME_KEY) || null;
    const subtotal = state.items.reduce((n, x) => n + (x.price || 0) * (x.quantity || 0), 0);
    const payload = {
      email,
      first_name: firstName,
      items: state.items.map((x) => ({ slug: x.slug, kind: x.kind, name: x.name, price: x.price, quantity: x.quantity })),
      subtotal,
    };
    snapshotTimer.current = setTimeout(() => {
      api.post("/leads/cart-snapshot", payload).catch(() => { /* silent — non-critical */ });
    }, 4000);
    return () => { if (snapshotTimer.current) clearTimeout(snapshotTimer.current); };
  }, [state.items, user?.email, user?.name]);

  const addItem = useCallback((item, opts = {}) => {
    const addQty = item.quantity || 1;
    const existing = itemsRef.current.find((x) => x.slug === item.slug && x.kind === item.kind);
    const duplicate = !!existing;
    const prevQty = existing ? existing.quantity : 0;
    const nextQty = prevQty + addQty;
    setState((s) => {
      const idx = s.items.findIndex(
        (x) => x.slug === item.slug && x.kind === item.kind
      );
      let items;
      if (idx >= 0) {
        items = s.items.map((x, i) => i === idx ? { ...x, quantity: x.quantity + addQty } : x);
      } else {
        items = [...s.items, { ...item, quantity: addQty }];
      }
      return { ...s, items };
    });
    // Oct 2026 (P3.4 duplicate line-item detection) — session recordings
    // showed double-taps silently turning one box into two and inflating
    // the total at checkout. When the SKU is already in the cart we say so
    // and offer a one-tap Undo that restores the previous quantity.
    if (duplicate && !opts.silentDuplicate) {
      const unit = item.unit === "kg" ? " kg" : "";
      toast(`${item.name} is already in your cart`, {
        description: `Quantity is now ${nextQty}${unit}. Didn't mean to add it twice?`,
        action: {
          label: "Undo",
          onClick: () => {
            setState((s) => ({
              ...s,
              items: s.items.map((x) => (x.slug === item.slug && x.kind === item.kind ? { ...x, quantity: prevQty } : x)),
            }));
          },
        },
        duration: 6000,
        id: `dup-${item.kind}-${item.slug}`,
      });
    }
    // ONE call → Meta pixel + CAPI, Clarity custom event, and our own
    // server-side funnel log. Single funnel for ALL add-to-cart entry
    // points (Product/Box detail, BYO, ProductCard, BoxCard, cart qty+).
    commerceAddToCart({ ...item, quantity: addQty });
    // NOTE: `opts.triggerUpsell` is intentionally IGNORED here. Aug 2026 UX
    // fix — the previous behaviour popped the subscription-upsell modal
    // after EVERY add-to-cart, and session recordings showed shoppers
    // aggressively dismissing it four or five times per session. The
    // upsell now fires once, from `Cart.jsx`, when the shopper reviews
    // their basket — see `useCartUpsellOnce` there.
    return { duplicate, quantity: nextQty, previousQuantity: prevQty };
  }, []);

  const updateQty = useCallback((slug, kind, quantity) => {
    setState((s) => ({
      ...s,
      items: s.items
        .map((x) => (x.slug === slug && x.kind === kind ? { ...x, quantity } : x))
        .filter((x) => x.quantity > 0),
    }));
  }, []);

  const removeItem = useCallback((slug, kind) => {
    setState((s) => ({ ...s, items: s.items.filter((x) => !(x.slug === slug && x.kind === kind)) }));
  }, []);

  const clear = useCallback(() => setState({ items: [], subscription: OFFER.subscribeDefaultOn, frequency: "monthly" }), []);

  const setSubscription = useCallback((subscription) => {
    setState((s) => ({ ...s, subscription }));
  }, []);

  const setFrequency = useCallback((frequency) => setState((s) => ({ ...s, frequency })), []);

  // Distinct line items (not summed quantity). Weight-priced cuts store
  // quantity in kg (e.g. 0.5), so `sum(quantity)` produced "0.5" on the
  // header badge and mobile cart bar. Shoppers understand "items" as
  // distinct cart lines.
  const count = useMemo(() => state.items.length, [state.items]);

  // Sept 2026 perf — memoise the actions object so ProductCard (and any
  // other component that only calls actions) doesn't re-render when cart
  // state changes. All action fns are useCallback([]) so deps stay stable.
  const actions = useMemo(
    () => ({ addItem, updateQty, removeItem, clear, setSubscription, setFrequency }),
    [addItem, updateQty, removeItem, clear, setSubscription, setFrequency]
  );

  return (
    <CartActionsContext.Provider value={actions}>
      <CartContext.Provider value={{
        ...state,
        addItem, updateQty, removeItem, clear,
        setSubscription, setFrequency,
        count,
        upsellOpen, setUpsellOpen,
      }}>
        {children}
      </CartContext.Provider>
    </CartActionsContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
// Sept 2026 — call this from components that ONLY need to mutate the
// cart (ProductCard, BoxCard, etc). Skips the re-render on state change.
export const useCartActions = () => useContext(CartActionsContext);
